import { useEffect } from 'react';
import { Temporal } from 'temporal-polyfill';
import { formatDuration } from '@airreading/common';
import { SessionStatus } from '@airreading/common/data';
import { ConcreteEvent } from './session-schedule-calendar';

export function getNextClassTooltipLabel(events: ConcreteEvent[], timeZone: string): string {
  const now = Temporal.Now.instant();
  const today = Temporal.Now.zonedDateTimeISO(timeZone).toPlainDate();

  const current = events.find(
    (e) => Temporal.PlainDate.compare(e.startDate, today) === 0 && e.eventInterval.contains(now),
  );

  if (current) {
    return current.status !== SessionStatus.cancelled ? 'Class in progress' : 'Class cancelled';
  }

  const upcomingToday = events
    .filter(
      (e) =>
        Temporal.PlainDate.compare(e.startDate, today) === 0 &&
        Temporal.Instant.compare(e.eventInterval.start, now) > 0,
    )
    .sort((a, b) => Temporal.Instant.compare(a.eventInterval.start, b.eventInterval.start));

  const [next] = upcomingToday;

  if (!next) return 'No more classes today';
  if (next.status === SessionStatus.cancelled) return 'Next class cancelled';

  const duration = next.eventInterval.start.since(now, { largestUnit: 'hours', smallestUnit: 'minutes' });
  return duration.total({ unit: 'minutes' }) <= 0 ? 'Class starting now' : `Next class in ${formatDuration(duration)}`;
}

function updateTooltip(events: ConcreteEvent[], timeZone: string) {
  const el = document.querySelector('.rbc-current-time-indicator');

  if (!el) return;

  const label = getNextClassTooltipLabel(events, timeZone);
  el.setAttribute('data-tooltip', label);
}

export function SessionNextClassTooltip({ events, timeZone }: { events: ConcreteEvent[]; timeZone: string }) {
  useEffect(() => {
    updateTooltip(events, timeZone);

    const interval = setInterval(() => {
      updateTooltip(events, timeZone);
    }, 60_000);

    const observer = new MutationObserver(() => {
      updateTooltip(events, timeZone);
    });

    const root = document.querySelector('.rbc-time-content');
    if (root) {
      observer.observe(root, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      clearInterval(interval);
      observer.disconnect();
    };
  }, [events, timeZone]);

  return null;
}
